import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { submitForm } from '@/features/games/launchGame.js';
import { GameRestrictionNotice } from '@/features/promo/GameRestrictionNotice.js';
import { toggleChatwootBubble } from '@/lib/chatwoot/index.js';
import type { LockableCard } from '@/features/promo/use-game-lock.js';
import type { LaunchMethod } from '@/features/games/api.js';

interface Props {
  url: string;
  method: LaunchMethod;
  payload: Record<string, unknown> | null;
  title: string;
  onBack: () => void;
  /**
   * The catalog identity of what is running in the frame, so a session that was
   * already open when a bonus started restricting this camp can say so (#546).
   * Optional: a host with nothing to identify shows no notice rather than
   * guessing one.
   */
  game?: LockableCard | null;
  /**
   * In-shell nav for frameless providers (the provider UI has no menu of its
   * own). Rendered right of the iframe on md+ and below it on mobile.
   */
  sideRail?: ReactNode;
}

const IFRAME_NAME = 'bf-seamless-iframe';

/**
 * Provider iframe that fills the whole content column (right of the sidebar,
 * below the navbar) edge-to-edge — breaking out of <main>'s max-width so a
 * sportsbook/lottery book reads as part of the site (betby-style), not a modal.
 * The offset is measured live from the content column so it tracks the layout
 * (sidebar present/hidden, navbar height) across resizes.
 */
export function SeamlessGameFrame({ url, method, payload, title, onBack, sideRail, game }: Props) {
  const { t } = useTranslation('common');
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [box, setBox] = useState<{ top: number; left: number; bottom: number }>({ top: 76, left: 0, bottom: 0 });

  useEffect(() => {
    function measure() {
      const col = document.querySelector('main')?.parentElement;
      if (!col) return;
      const r = col.getBoundingClientRect();
      // The mobile bottom nav overlays fixed at bottom:0 (later in DOM, same
      // z-index) — lift the frame above it so nothing hides behind the bar.
      const nav = document.querySelector('[data-bf-mobile-nav]');
      const navH = nav ? Math.round(nav.getBoundingClientRect().height) : 0;
      setBox({ top: Math.max(0, Math.round(r.top)), left: Math.max(0, Math.round(r.left)), bottom: navH });
    }
    measure();
    window.addEventListener('resize', measure);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    toggleChatwootBubble('hide');
    return () => {
      window.removeEventListener('resize', measure);
      document.body.style.overflow = prevOverflow;
      toggleChatwootBubble('show');
    };
  }, []);

  useEffect(() => {
    if (method !== 'POST' || !iframeRef.current) return;
    submitForm(url, payload ?? {}, IFRAME_NAME);
  }, [method, url, payload]);

  return (
    <div
      className="fixed z-30 flex flex-col bg-[var(--bg)]"
      style={{ top: box.top, left: box.left, right: 0, bottom: box.bottom }}
    >
      <div className="flex shrink-0 items-center gap-3 border-b border-[var(--border)] bg-[var(--bg-raised)] px-3 py-2">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex h-9 items-center gap-2 rounded-lg bg-[var(--bg-elevated)] px-3.5 text-[13px] font-bold text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-sunken)] hover:text-[var(--text-primary)]"
        >
          <ArrowLeft size={16} />
          {t('common.back')}
        </button>
        <span className="truncate text-[14px] font-extrabold text-[var(--text-primary)]">{title}</span>
      </div>
      <GameRestrictionNotice game={game} />
      <div className="flex min-h-0 flex-1 flex-col md:flex-row">
        <iframe
          ref={iframeRef}
          name={IFRAME_NAME}
          src={method === 'GET' ? url : 'about:blank'}
          title={title}
          className="min-h-0 min-w-0 w-full flex-1 border-0 bg-black"
          allow="autoplay; fullscreen; payment"
          allowFullScreen
        />
        {sideRail}
      </div>
    </div>
  );
}
